/**
 * Things the ranger works rather than blows up: the field telephone exchange (mission 22), the
 * breaker panels of the switch yard (33) and the strike beacon (35). The panel and the beacon have
 * two states and a self-lit part each; `world/propModels.ts` says which builder is which state.
 */

import type { Palette } from '../palette';
import type { KitBuilder } from './flora';
import { MeshBuilder, place } from './MeshBuilder';
import { sided, strut, wheel } from './parts';
import { capColor } from './snow';

/**
 * Field exchange: an olive cabinet with its doors folded back on a switchboard, a cable drum stood
 * beside it and another lying behind, and the pole the lines come in on rising behind the cabinet.
 */
export const telephoneExchange: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const cx = -0.4;
  mb.box(1.45, 0.15, 0.9, p.color('concrete.lo'), place(cx, 0, -0.18));
  mb.box(1.3, 1.5, 0.72, sided(p, 'canvas'), place(cx, 0.15, -0.18));
  mb.box(1.5, 0.08, 1.0, { all: p.color('metal.lo'), top: capColor(p, 'metal.mid') }, place(cx, 1.65, -0.1));
  // Doors folded back; the board: dark, two rows of jacks, three call lamps.
  for (const sx of [-1, 1]) mb.box(0.05, 1.35, 0.55, p.color('canvas.lo'), place(cx + sx * 0.66, 0.22, 0.45));
  mb.box(1.15, 1.2, 0.05, p.color('ink.1'), place(cx, 0.35, 0.19));
  for (const y of [0.62, 0.95]) mb.box(0.95, 0.1, 0.05, p.color('metal.hi'), place(cx, y, 0.22));
  for (const dx of [-0.3, 0, 0.3]) mb.box(0.12, 0.1, 0.05, p.color('fire.2'), place(cx + dx, 1.3, 0.22));

  // Drum on its rim: two plank flanges and the black cable wound between them.
  for (const dx of [-0.2, 0.2]) wheel(mb, p, [0.72 + dx, 0.47, 0.32], 0.48, 0.06, 6, 'timber.mid');
  wheel(mb, p, [0.72, 0.47, 0.32], 0.3, 0.36, 6, 'ink.0');
  // The spare, lying flat behind it.
  mb.cylinder(0.42, 0.42, 0.07, 6, p.color('timber.lo'), place(0.62, 0, -0.42), p.color('timber.mid'));
  mb.cylinder(0.26, 0.26, 0.3, 6, p.color('ink.1'), place(0.62, 0.07, -0.42));
  mb.cylinder(0.42, 0.42, 0.07, 6, p.color('timber.lo'), place(0.62, 0.37, -0.42), capColor(p, 'timber.mid'));

  // Pole, crossarm and insulators; the drop wire down to the cabinet and the line off to the drum.
  mb.cylinder(0.09, 0.07, 3.6, 5, p.color('timber.lo'), place(cx, 0, -0.72));
  mb.box(1.1, 0.1, 0.1, p.color('timber.lo'), place(cx, 3.1, -0.72));
  for (const sx of [-1, 1]) mb.box(0.1, 0.16, 0.1, p.color('paper.0'), place(cx + sx * 0.45, 3.2, -0.72));
  strut(mb, [cx + 0.45, 3.2, -0.72], [cx + 0.4, 1.73, -0.3], 0.05, p.color('ink.1'));
  strut(mb, [cx + 0.65, 1.2, 0], [0.72, 0.8, 0.32], 0.05, p.color('ink.1'));
  return mb;
};

/* ------------------------------------------------------------------------------ breaker panel */

const PANEL_FACE = 0.13;
const PANEL_LAMP = { x: -0.25, y: 1.0 };

function breaker(p: Palette, live: boolean): MeshBuilder {
  const mb = new MeshBuilder();
  mb.box(1.1, 0.12, 0.5, p.color('concrete.lo'), place(0, 0, -0.05));
  mb.box(1.0, 1.4, 0.42, sided(p, 'metal', capColor(p, 'metal.hi')), place(0, 0.12, -0.08));
  mb.box(1.02, 0.14, 0.44, p.color('fire.2'), place(0, 1.24, -0.08));
  // The lever in its slot: UP and lamp lit while the circuit is live; thrown, it hangs DOWN and the lamp is dark.
  mb.box(0.14, 0.62, 0.04, p.color('ink.0'), place(0.25, 0.5, PANEL_FACE + 0.01));
  const pivot = [0.25, 0.8, PANEL_FACE] as const;
  const knob = [0.25, live ? 1.02 : 0.58, PANEL_FACE + 0.17] as const;
  strut(mb, pivot, knob, 0.07, p.color('metal.lo'));
  mb.box(0.16, 0.16, 0.16, p.color('playerAccent.0'), place(knob[0], knob[1] - 0.08, knob[2]));
  mb.box(0.2, 0.2, 0.1, p.color(live ? 'fire.2' : 'ink.1'), place(PANEL_LAMP.x, PANEL_LAMP.y - 0.1, PANEL_FACE + 0.03));
  // Insulator stacks and the busbar across them: outdoor switchgear, not a locker.
  const china = p.color('paper.0');
  for (const x of [-0.3, 0, 0.3]) mb.cylinder(0.09, 0.06, 0.34, 5, china, place(x, 1.52, -0.08), p.color('metal.lo'));
  mb.box(0.8, 0.05, 0.05, p.color('metal.lo'), place(0, 1.86, -0.08));
  return mb;
}

/** Breaker panel, live: lever up, lamp lit. The knob is the player's colour - it is his to throw. */
export const breakerPanel: KitBuilder = (p) => breaker(p, true);
/** The same panel thrown: lever down, lamp dark. */
export const breakerPanelOff: KitBuilder = (p) => breaker(p, false);

/** The panel lamp alone, a shade proud of the body's: the self-lit part. */
export const breakerLamp: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.24, 0.24, 0.1, p.color('fire.2'), place(PANEL_LAMP.x, PANEL_LAMP.y - 0.12, PANEL_FACE + 0.05));
  return mb;
};

/* ------------------------------------------------------------------------------ strike beacon */

const STROBE_Y = 0.7;

/**
 * Strike beacon, set up: a knee-high tripod carrying the transmitter and an amber strobe - OURS, so
 * the player's colour. Exactly the small budget: three legs, a box, a lamp.
 */
export const strikeBeacon: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  for (let i = 0; i < 3; i++) {
    const a = (i / 3) * Math.PI * 2 + Math.PI / 3;
    const foot = [Math.sin(a) * 0.27, 0, Math.cos(a) * 0.27] as const;
    strut(mb, foot, [Math.sin(a) * 0.04, 0.52, Math.cos(a) * 0.04], 0.05, p.color('metal.lo'));
  }
  mb.box(0.22, 0.2, 0.2, { all: p.color('canvas.lo'), top: capColor(p, 'canvas.mid') }, place(0, 0.5, 0));
  mb.box(0.14, 0.16, 0.14, p.color('playerAccent.0'), place(0, STROBE_Y, 0));
  return mb;
};

/** Before the beacon is placed the prop is only the SPOT: a small amber cross on the ground, like the pickup's X. */
export const strikeBeaconSpot: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  const amber = p.color('playerAccent.0');
  // Thick for a painted mark: props are seated 5 cm into the ground, and this must still show on a slope.
  for (const yaw of [Math.PI / 4, -Math.PI / 4]) mb.box(0.62, 0.14, 0.12, amber, place(0, 0, 0, yaw));
  return mb;
};

/** The strobe alone, a shade larger than the body's: the self-lit part, which the world flashes. */
export const strikeBeaconStrobe: KitBuilder = (p) => {
  const mb = new MeshBuilder();
  mb.box(0.18, 0.18, 0.18, p.color('playerAccent.0'), place(0, STROBE_Y - 0.01, 0));
  return mb;
};
